/**
 * Browser export.
 *
 * Primary path (WebCodecs, non-realtime): mediabunny decodes each pandora
 * source (Input + CanvasSink/AudioBufferSink over the same direct/proxied URLs
 * the preview uses), frames are composited with the SHARED compositor onto an
 * OffscreenCanvas and encoded (mp4: avc+aac, webm: vp9/vp8+opus) via
 * CanvasSource/AudioBufferSource into a BufferTarget.
 *
 * Fallback path (always available): realtime capture — the preview engine
 * plays the edit once while canvas.captureStream + WebAudio stream destination
 * feed a MediaRecorder (webm).
 */
import {
  ALL_FORMATS,
  AudioBufferSink,
  AudioBufferSource,
  CanvasSink,
  CanvasSource,
  getFirstEncodableAudioCodec,
  getFirstEncodableVideoCodec,
  Input,
  Mp4OutputFormat,
  Output,
  BufferTarget,
  QUALITY_HIGH,
  UrlSource,
  WebMOutputFormat,
  type WrappedCanvas,
} from 'mediabunny';
import type { Archive } from '../api/archives';
import { clipDuration, totalDuration } from '../format/helpers';
import type { Edit } from '../format/types';
import {
  audioClipGainAt,
  clipAudioGainAt,
  drawFrame,
  type Ctx2D,
  type Drawable,
} from './compositor';
import { sourceMediaUrl } from './mediaurls';
import type { PreviewEngine } from './preview';

export interface ExportProgress {
  phase: 'preparing' | 'audio' | 'video' | 'finalizing' | 'recording';
  framesDone: number;
  framesTotal: number;
}

export interface ExportOptions {
  container: 'mp4' | 'webm';
  width: number;
  height: number;
  /** Project name, used to name the downloaded file. Falls back to "export". */
  title?: string;
  onProgress: (p: ExportProgress) => void;
}

export interface ExportResult {
  blob: Blob;
  filename: string;
  warnings: string[];
}

export class ExportCancelledError extends Error {
  constructor() {
    super('export cancelled');
  }
}

export interface CancelToken {
  cancelled: boolean;
}

export function webCodecsAvailable(): boolean {
  return typeof VideoEncoder !== 'undefined' && typeof AudioEncoder !== 'undefined';
}

function absoluteUrl(url: string): string {
  return new URL(url, window.location.href).toString();
}

// ---------------------------------------------------------------------------
// Primary: mediabunny / WebCodecs
// ---------------------------------------------------------------------------

interface ClipFrame {
  sink: CanvasSink;
  last: WrappedCanvas | null;
}

interface SourceReader {
  input: Input;
  videoTrack: Awaited<ReturnType<Input['getPrimaryVideoTrack']>> | null;
  audioSink: AudioBufferSink | null;
  /**
   * One CanvasSink + cached frame PER CLIP: the same source can be active on
   * two tracks at two different media times in one output frame, and per-clip
   * sinks also avoid seek thrash between distant timeline positions.
   */
  clips: Map<string, ClipFrame>;
}

export async function exportWithMediabunny(
  edit: Edit,
  archives: Archive[],
  opts: ExportOptions,
  cancel: CancelToken,
): Promise<ExportResult> {
  const warnings: string[] = [];
  const duration = totalDuration(edit);
  if (duration <= 0) throw new Error('Timeline is empty');
  const fps = edit.settings.fps;
  const framesTotal = Math.max(1, Math.ceil(duration * fps));
  opts.onProgress({ phase: 'preparing', framesDone: 0, framesTotal });

  // Render at the requested export resolution
  const renderEdit: Edit = {
    ...edit,
    settings: { ...edit.settings, width: opts.width, height: opts.height },
  };

  const format = opts.container === 'mp4' ? new Mp4OutputFormat() : new WebMOutputFormat();
  const videoCodec = await getFirstEncodableVideoCodec(format.getSupportedVideoCodecs(), {
    width: opts.width,
    height: opts.height,
  });
  if (!videoCodec) throw new Error(`No encodable video codec for ${opts.container}`);
  const audioCodec = await getFirstEncodableAudioCodec(format.getSupportedAudioCodecs(), {
    numberOfChannels: 2,
    sampleRate: 48000,
  });
  if (!audioCodec) warnings.push('No encodable audio codec — exporting video only');

  const output = new Output({ format, target: new BufferTarget() });
  const canvas = new OffscreenCanvas(opts.width, opts.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  const videoSource = new CanvasSource(canvas, { codec: videoCodec, bitrate: QUALITY_HIGH });
  output.addVideoTrack(videoSource, { frameRate: fps });
  const audioSource = audioCodec
    ? new AudioBufferSource({ codec: audioCodec, bitrate: QUALITY_HIGH })
    : null;
  if (audioSource) output.addAudioTrack(audioSource);
  await output.start();

  // Open one Input per pandora source
  const readers = new Map<string, SourceReader>();
  const images = new Map<string, ImageBitmap>();
  try {
    for (const [id, src] of Object.entries(edit.sources)) {
      if (cancel.cancelled) throw new ExportCancelledError();
      if (src.kind === 'pandora-video') {
        const url = sourceMediaUrl(src, archives);
        if (!url) {
          warnings.push(`Source ${id}: unknown archive ${src.archive}`);
          continue;
        }
        const input = new Input({
          source: new UrlSource(absoluteUrl(url)),
          formats: ALL_FORMATS,
        });
        let videoTrack: SourceReader['videoTrack'] = null;
        let audioSink: AudioBufferSink | null = null;
        try {
          videoTrack = await input.getPrimaryVideoTrack();
          const at = await input.getPrimaryAudioTrack();
          if (at) audioSink = new AudioBufferSink(at);
        } catch (e) {
          warnings.push(
            `Source ${src.title || src.item}: cannot read media (${e instanceof Error ? e.message : e})`,
          );
        }
        readers.set(id, { input, videoTrack, audioSink, clips: new Map() });
      } else {
        try {
          const res = await fetch(src.url);
          images.set(id, await createImageBitmap(await res.blob()));
        } catch {
          warnings.push(`Image source ${id} failed to load`);
        }
      }
    }

    // ---- audio ----
    if (audioSource) {
      opts.onProgress({ phase: 'audio', framesDone: 0, framesTotal });
      const mixed = await mixAudio(edit, readers, duration, cancel, warnings);
      const CHUNK = 48000 * 5;
      for (let off = 0; off < mixed[0].length; off += CHUNK) {
        if (cancel.cancelled) throw new ExportCancelledError();
        const len = Math.min(CHUNK, mixed[0].length - off);
        const buf = new AudioBuffer({ length: len, numberOfChannels: 2, sampleRate: 48000 });
        buf.copyToChannel(mixed[0].subarray(off, off + len), 0);
        buf.copyToChannel(mixed[1].subarray(off, off + len), 1);
        await audioSource.add(buf);
      }
      audioSource.close();
    }

    // ---- video ----
    const provider = makeExportProvider(readers, images);
    for (let frame = 0; frame < framesTotal; frame++) {
      if (cancel.cancelled) throw new ExportCancelledError();
      const t = frame / fps;
      await prefetchFrames(edit, readers, t);
      drawFrame(ctx as unknown as Ctx2D, renderEdit, t, provider);
      await videoSource.add(t, 1 / fps);
      if (frame % 5 === 0 || frame === framesTotal - 1)
        opts.onProgress({ phase: 'video', framesDone: frame + 1, framesTotal });
    }
    videoSource.close();

    opts.onProgress({ phase: 'finalizing', framesDone: framesTotal, framesTotal });
    await output.finalize();
    const buffer = (output.target as BufferTarget).buffer;
    if (!buffer) throw new Error('muxer produced no output');
    const mime = opts.container === 'mp4' ? 'video/mp4' : 'video/webm';
    return {
      blob: new Blob([buffer], { type: mime }),
      filename: exportFilename(opts.title, opts.container),
      warnings,
    };
  } catch (e) {
    if (output.state === 'started') await output.cancel().catch(() => undefined);
    throw e;
  } finally {
    for (const r of readers.values()) {
      r.clips.clear();
      void r.input.dispose?.();
    }
    for (const img of images.values()) img.close();
  }
}

/** Seek/decode the needed source frame(s) for time t into each clip's cache. */
async function prefetchFrames(
  edit: Edit,
  readers: Map<string, SourceReader>,
  t: number,
): Promise<void> {
  for (const track of edit.tracks) {
    if (track.kind !== 'video') continue;
    for (const clip of track.clips) {
      const dur = clipDuration(clip);
      if (t < clip.start || t >= clip.start + dur) continue;
      const reader = readers.get(clip.source);
      if (!reader?.videoTrack) continue;
      let cf = reader.clips.get(clip.id);
      if (!cf) {
        cf = { sink: new CanvasSink(reader.videoTrack, { poolSize: 2 }), last: null };
        reader.clips.set(clip.id, cf);
      }
      const mediaTime = clip.in + (t - clip.start) * clip.speed;
      if (cf.last && mediaTime >= cf.last.timestamp && mediaTime < cf.last.timestamp + cf.last.duration)
        continue; // cached frame still covers this time
      try {
        const wrapped = await cf.sink.getCanvas(mediaTime);
        if (wrapped) cf.last = wrapped;
      } catch {
        // decode error — keep last frame
      }
    }
  }
}

function makeExportProvider(
  readers: Map<string, SourceReader>,
  images: Map<string, ImageBitmap>,
): (sourceId: string, clipId?: string) => Drawable | null {
  return (sourceId, clipId) => {
    const reader = readers.get(sourceId);
    if (reader) {
      const cf = clipId
        ? reader.clips.get(clipId)
        : reader.clips.values().next().value;
      return (cf?.last?.canvas as Drawable | undefined) ?? null;
    }
    return images.get(sourceId) ?? null;
  };
}

/**
 * Mix all clip audio into stereo 48k Float32 buses with per-sample gain
 * envelopes (volume/gain, fades, equal-power transition crossfades).
 *
 * `speed` is a VARISPEED: the source is resampled by the speed ratio, so pitch
 * shifts with it. Preview does the same (`preservesPitch = false` on the pooled
 * elements), and the two must stay in step — a pitch-preserving time-stretch
 * here would make exports disagree with what the editor plays.
 */
export async function mixAudio(
  edit: Edit,
  readers: Map<string, SourceReader>,
  duration: number,
  cancel: CancelToken,
  warnings: string[],
): Promise<[Float32Array, Float32Array]> {
  const SR = 48000;
  const totalSamples = Math.ceil(duration * SR);
  const bus: [Float32Array, Float32Array] = [
    new Float32Array(totalSamples),
    new Float32Array(totalSamples),
  ];

  interface AudioJob {
    sourceId: string;
    srcIn: number;
    srcOut: number;
    start: number;
    /** Source seconds per timeline second (see the varispeed note above). */
    speed: number;
    gainAt: (t: number) => number;
  }
  const jobs: AudioJob[] = [];
  edit.tracks.forEach((track, trackIndex) => {
    if (track.kind === 'video') {
      if (track.muted) return;
      for (const clip of track.clips) {
        if (clip.muted || clip.volume <= 0) continue;
        jobs.push({
          sourceId: clip.source,
          srcIn: clip.in,
          srcOut: clip.out,
          start: clip.start,
          speed: clip.speed ?? 1,
          gainAt: (t) => clipAudioGainAt(edit, trackIndex, clip, t),
        });
      }
    } else if (track.kind === 'audio') {
      if (track.muted) return;
      for (const clip of track.clips) {
        jobs.push({
          sourceId: clip.source,
          srcIn: clip.in,
          srcOut: clip.out,
          start: clip.start,
          speed: clip.speed ?? 1,
          gainAt: (t) => audioClipGainAt(edit, trackIndex, clip.id, t),
        });
      }
    }
  });

  // A source with no decodable audio track drops out of the mix entirely.
  // Say so once per source rather than exporting quiet video and no reason.
  const silentSources = new Set<string>();
  for (const job of jobs) {
    const reader = readers.get(job.sourceId);
    if (!reader?.audioSink) {
      if (!silentSources.has(job.sourceId)) {
        silentSources.add(job.sourceId);
        warnings.push(`Source ${job.sourceId}: no audio track — clips from it export silent`);
      }
      continue;
    }
    for await (const wrapped of reader.audioSink.buffers(job.srcIn, job.srcOut)) {
      if (cancel.cancelled) throw new ExportCancelledError();
      const { buffer, timestamp } = wrapped;
      const srcRate = buffer.sampleRate;
      const chL = buffer.getChannelData(0);
      const chR = buffer.numberOfChannels > 1 ? buffer.getChannelData(1) : chL;
      // clamp source range to [srcIn, srcOut]
      const segStart = Math.max(timestamp, job.srcIn);
      const segEnd = Math.min(timestamp + buffer.duration, job.srcOut);
      if (segEnd <= segStart) continue;
      // Timeline time advances 1/speed as fast as source time.
      const sp = job.speed;
      const tlStart = job.start + (segStart - job.srcIn) / sp;
      const outStart = Math.max(0, Math.round(tlStart * SR));
      const outEnd = Math.min(totalSamples, Math.round((tlStart + (segEnd - segStart) / sp) * SR));
      let gain = 0;
      for (let i = outStart; i < outEnd; i++) {
        if ((i - outStart) % 128 === 0) gain = job.gainAt(i / SR);
        // linear-interp resample from source, stepping `sp` source-seconds
        // per timeline-second (varispeed — pitch shifts with speed)
        const srcT = segStart + ((i - outStart) / SR) * sp - timestamp;
        const pos = srcT * srcRate;
        const i0 = Math.floor(pos);
        const frac = pos - i0;
        const i1 = Math.min(i0 + 1, chL.length - 1);
        if (i0 < 0 || i0 >= chL.length) continue;
        bus[0][i] += (chL[i0] * (1 - frac) + chL[i1] * frac) * gain;
        bus[1][i] += (chR[i0] * (1 - frac) + chR[i1] * frac) * gain;
      }
    }
  }

  // soft master clip
  for (const ch of bus) {
    for (let i = 0; i < ch.length; i++) {
      const v = ch[i];
      if (v > 1) ch[i] = 1;
      else if (v < -1) ch[i] = -1;
    }
  }
  return bus;
}

// ---------------------------------------------------------------------------
// Fallback: realtime MediaRecorder capture
// ---------------------------------------------------------------------------

export function exportRealtime(
  engine: PreviewEngine,
  edit: Edit,
  opts: Pick<ExportOptions, 'onProgress' | 'title'>,
  cancel: CancelToken,
): Promise<ExportResult> {
  return new Promise((resolve, reject) => {
    const duration = totalDuration(edit);
    if (duration <= 0) {
      reject(new Error('Timeline is empty'));
      return;
    }
    const fps = edit.settings.fps;
    const framesTotal = Math.ceil(duration * fps);
    const canvasStream = engine.getCanvas().captureStream(fps);
    const audioStream = engine.getAudioStream();
    const stream = new MediaStream([
      ...canvasStream.getVideoTracks(),
      ...audioStream.getAudioTracks(),
    ]);
    const mime = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'].find(
      (m) => MediaRecorder.isTypeSupported(m),
    );
    const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size) chunks.push(e.data);
    };

    const prevOnEnded = engine.onEnded;
    const prevOnTime = engine.onTime;
    let pollId = 0;

    const cleanup = () => {
      clearInterval(pollId);
      engine.onEnded = prevOnEnded;
      engine.onTime = prevOnTime;
      for (const t of stream.getTracks()) t.stop();
    };

    recorder.onstop = () => {
      cleanup();
      if (cancel.cancelled) {
        reject(new ExportCancelledError());
        return;
      }
      resolve({
        blob: new Blob(chunks, { type: 'video/webm' }),
        filename: exportFilename(opts.title, 'webm'),
        warnings: ['Realtime capture (MediaRecorder): webm only, plays the edit once through'],
      });
    };
    recorder.onerror = () => {
      cleanup();
      engine.pause();
      reject(new Error('MediaRecorder failed'));
    };

    engine.onEnded = () => {
      recorder.stop();
    };
    engine.onTime = (t) => {
      prevOnTime?.(t);
      opts.onProgress({
        phase: 'recording',
        framesDone: Math.min(framesTotal, Math.floor(t * fps)),
        framesTotal,
      });
    };
    pollId = window.setInterval(() => {
      if (cancel.cancelled) {
        engine.pause();
        recorder.stop();
      }
    }, 200);

    engine.seek(0);
    recorder.start(250);
    engine.play();
  });
}

/**
 * Build the download filename from the project name.
 *
 * Deliberately keeps non-ASCII letters instead of stripping to `[a-z0-9]`: this
 * is an editor over Indian film archives, so Devanagari, Bangla and Tamil
 * project names are ordinary here, and stripping them would leave every such
 * project exporting as the same fallback. Only characters that are genuinely
 * unsafe in a filename are replaced.
 */
export function exportFilename(title: string | undefined, container: string): string {
  const slug = (title ?? '')
    .normalize('NFC')
    // path separators, Windows-reserved characters, and control characters
    // eslint-disable-next-line no-control-regex
    .replace(/[/\\:*?"<>|\u0000-\u001f]/g, ' ')
    .replace(/[\s_]+/g, '_') // whitespace runs -> single underscore
    .replace(/^[._]+|[._]+$/g, '') // no leading dot (hidden file) or stray edges
    .toLowerCase()
    .slice(0, 80)
    .replace(/_+$/, ''); // slice may have landed mid-separator
  return `${slug || 'export'}.${container}`;
}

/** Trigger a browser download for an export result. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
